/**
 * Notificação do chat no aparelho (Web Push) — o lado do app.
 *
 * Quem recebe mensagem é avisado no aparelho mesmo com o PharuERP fechado ou
 * com outra janela na frente. A inscrição é de UMA pessoa neste navegador:
 *
 *  - quem liga é a pessoa, com um clique (o navegador só deixa pedir a
 *    permissão a partir de um gesto);
 *  - a escolha fica guardada POR PESSOA. A chave NÃO usa o prefixo
 *    `pharuerp:` de propósito: o logout apaga tudo que tem esse prefixo, e a
 *    escolha precisa sobreviver a ele — quem ligou não liga de novo a cada
 *    login. Ela não é dado operacional: é "sim" ou nada, e só vale para a
 *    própria pessoa;
 *  - saiu da conta, a inscrição é DESFEITA no navegador (o endereço morre):
 *    nenhum aviso de quem saiu chega na vez de quem entrar;
 *  - o service worker confere de novo antes de mostrar
 *    (`public/service-worker.js`), pelo bilhete `/__push/dono`;
 *  - o aviso mostra quem mandou, nunca o texto da mensagem.
 */

export const CACHE_DO_PUSH = "pharu-push";
export const DONO = "/__push/dono";
const PREFIXO_DA_ESCOLHA = "pharu-push:escolha:";
const ESPERA_PELO_SERVICE_WORKER_MS = 4000;

export function temSuporte() {
  return (
    typeof window !== "undefined" &&
    typeof navigator !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window &&
    "caches" in window
  );
}

export function escolheuLigar(usuarioId) {
  if (!usuarioId) return false;
  try {
    return window.localStorage.getItem(`${PREFIXO_DA_ESCOLHA}${usuarioId}`) === "1";
  } catch {
    return false;
  }
}

function guardarEscolha(usuarioId, ligar) {
  if (!usuarioId) return;
  try {
    if (ligar) window.localStorage.setItem(`${PREFIXO_DA_ESCOLHA}${usuarioId}`, "1");
    else window.localStorage.removeItem(`${PREFIXO_DA_ESCOLHA}${usuarioId}`);
  } catch {
    /* sem storage: vale só nesta sessão */
  }
}

// "Agora não" no convite do cabeçalho: por pessoa e fora do prefixo
// `pharuerp:`, pelo mesmo motivo da escolha — o logout não pode trazê-lo de volta.
const PREFIXO_DO_CONVITE = "pharu-push:convite-dispensado:";

export function dispensouConvite(usuarioId) {
  if (!usuarioId) return true;
  try {
    return window.localStorage.getItem(`${PREFIXO_DO_CONVITE}${usuarioId}`) === "1";
  } catch {
    return false;
  }
}

export function dispensarConvite(usuarioId) {
  if (!usuarioId) return;
  try {
    window.localStorage.setItem(`${PREFIXO_DO_CONVITE}${usuarioId}`, "1");
  } catch {
    /* sem storage: volta na próxima sessão */
  }
}

export function chaveEmBytes(base64url) {
  const base64 = (base64url + "=".repeat((4 - (base64url.length % 4)) % 4)).replace(/-/g, "+").replace(/_/g, "/");
  const bruto = window.atob(base64);
  const bytes = new Uint8Array(bruto.length);
  for (let i = 0; i < bruto.length; i += 1) bytes[i] = bruto.charCodeAt(i);
  return bytes;
}

const dados = (payload) => (payload && payload.data !== undefined ? payload.data : payload);

// Uma operação por vez: soltar a inscrição de quem saiu e inscrever quem
// entrou mexem no MESMO registro do navegador.
let fila = Promise.resolve();

function naFila(tarefa) {
  const proxima = fila.then(tarefa, tarefa);
  fila = proxima.catch(() => undefined);
  return proxima;
}

async function registro() {
  if (!temSuporte()) return null;
  const espera = new Promise((resolve) => {
    window.setTimeout(() => resolve(null), ESPERA_PELO_SERVICE_WORKER_MS);
  });
  return Promise.race([navigator.serviceWorker.ready, espera]);
}

async function escreverBilhete(para) {
  const cache = await window.caches.open(CACHE_DO_PUSH);
  // Conta pessoal: o bilhete não vence (`ate: null`); sai no logout.
  await cache.put(DONO, new Response(JSON.stringify({ para, ate: null }), { headers: { "Content-Type": "application/json" } }));
}

export function mesmaChave(buffer, bytes) {
  if (!buffer || !bytes) return false;
  const atual = new Uint8Array(buffer);
  return atual.length === bytes.length && atual.every((b, i) => b === bytes[i]);
}

async function apagarBilhete() {
  try {
    const cache = await window.caches.open(CACHE_DO_PUSH);
    await cache.delete(DONO);
  } catch {
    /* sem cache: nada a apagar */
  }
}

async function soltar() {
  if (!temSuporte()) return;
  try {
    const reg = await registro();
    const inscricao = reg ? await reg.pushManager.getSubscription() : null;
    if (inscricao) await inscricao.unsubscribe();
    const cache = await window.caches.open(CACHE_DO_PUSH);
    await cache.delete(DONO);
  } catch {
    /* nada inscrito */
  }
}

async function inscrever(request, usuarioId) {
  if (!temSuporte()) return "sem_suporte";
  const permissao = window.Notification.permission === "granted" ? "granted" : await window.Notification.requestPermission();
  if (permissao === "denied") return "bloqueadas";
  if (permissao !== "granted") return "desligadas";
  const reg = await registro();
  if (!reg) throw new Error("O app ainda está se preparando neste aparelho. Recarregue a página e tente de novo.");
  const { chave_publica: chave } = dados(await request("/api/chat/push/chave", { silentError: true }));
  if (!chave) throw new Error("As notificações ainda não estão ligadas no servidor.");
  // A MESMA inscrição do navegador serve enquanto a chave do servidor não
  // muda: o servidor só troca o dono (update_or_create pelo endereço). Antes
  // cada página aberta cancelava e criava outra, e o aviso que chegava no
  // meio da troca caía num endereço morto (2026-10-28).
  const antiga = await reg.pushManager.getSubscription();
  const reaproveita = antiga && mesmaChave(antiga.options?.applicationServerKey, chaveEmBytes(chave));
  if (antiga && !reaproveita) await antiga.unsubscribe();
  const inscricao = reaproveita ? antiga : await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: chaveEmBytes(chave) });
  try {
    const { para } = dados(
      await request("/api/chat/push/inscricao", { method: "POST", body: JSON.stringify(inscricao.toJSON()), silentError: true })
    );
    await escreverBilhete(para);
  } catch (erro) {
    // O servidor não confirmou de quem é: sem bilhete o aparelho não mostra
    // nada (nem aviso de quem estava antes), mas a inscrição fica para a
    // próxima tentativa — desfazê-la deixava o computador mudo.
    await apagarBilhete();
    if (!reaproveita) await inscricao.unsubscribe().catch(() => undefined);
    throw erro;
  }
  guardarEscolha(usuarioId, true);
  return "ligadas";
}

/** "sem_suporte" | "bloqueadas" | "desligadas" | "ligadas" */
export async function estadoAtual(usuarioId) {
  if (!temSuporte()) return "sem_suporte";
  if (window.Notification.permission === "denied") return "bloqueadas";
  if (window.Notification.permission !== "granted" || !escolheuLigar(usuarioId)) return "desligadas";
  const reg = await registro();
  const inscricao = reg ? await reg.pushManager.getSubscription() : null;
  return inscricao ? "ligadas" : "desligadas";
}

/** Ligou ou desligou em algum lugar (Chat ou cabeçalho): as outras peças conferem de novo. */
export const EVENTO_AVISOS_MUDARAM = "pharu:avisos-do-chat";

function avisarQueMudou(estado) {
  window.dispatchEvent(new Event(EVENTO_AVISOS_MUDARAM));
  return estado;
}

export function ligar(request, usuarioId) {
  return naFila(() => inscrever(request, usuarioId)).then(avisarQueMudou);
}

export function desligar(request, usuarioId) {
  return naFila(async () => {
    guardarEscolha(usuarioId, false);
    const reg = await registro();
    const inscricao = reg ? await reg.pushManager.getSubscription() : null;
    if (inscricao) {
      await request("/api/chat/push/inscricao", {
        method: "DELETE",
        body: JSON.stringify({ endpoint: inscricao.endpoint }),
        silentError: true,
      }).catch(() => undefined);
    }
    await soltar();
    return temSuporte() && window.Notification.permission === "denied" ? "bloqueadas" : "desligadas";
  }).then(avisarQueMudou);
}

/** Saiu da conta (ou a sessão caiu): o endereço deste navegador morre. */
export function soltarDoAparelho() {
  return naFila(soltar);
}

/**
 * Entrou alguém: se esta pessoa já tinha ligado aqui e o navegador ainda
 * permite, inscreve de novo como ela; senão garante que não sobrou a
 * inscrição de outra pessoa.
 */
export function acertarComQuemEstaAqui(request, usuarioId, temChat) {
  return naFila(async () => {
    if (!temSuporte()) return;
    const quer = Boolean(temChat) && escolheuLigar(usuarioId) && window.Notification.permission === "granted";
    if (!quer) {
      await soltar();
      return;
    }
    try {
      await inscrever(request, usuarioId);
    } catch {
      // Rede ou servidor fora agora: a inscrição fica (sem bilhete, nada aparece) e a próxima abertura tenta de novo.
    }
  });
}

/** Conversas com mensagem não lida — o número do menu e do sino (não o de mensagens). */
export function conversasComNaoLidas(porConversa) {
  if (!porConversa || typeof porConversa !== "object") return 0;
  return Object.values(porConversa).filter((n) => Number(n) > 0).length;
}

/** A página do Chat avisa o sino na hora (`detail.conversas`), sem esperar a volta de 60 s. */
export const EVENTO_NAO_LIDAS_DO_CHAT = "pharu:chat-nao-lidas";

let recontando = null;

/**
 * Fora da página do Chat: pergunta ao servidor e avisa o botão, o sino e o
 * menu. Uma pergunta por vez — o push e o relógio podem chamar juntos.
 */
export function recontarNaoLidasDoChat(request) {
  recontando ??= request("/api/chat/nao-lidas", { silentError: true })
    .then((p) => {
      const quantas = conversasComNaoLidas(dados(p)?.por_conversa);
      window.dispatchEvent(new CustomEvent(EVENTO_NAO_LIDAS_DO_CHAT, { detail: { conversas: quantas } }));
    })
    .catch(() => undefined)
    .finally(() => {
      recontando = null;
    });
  return recontando;
}

export function avisarNaoLidasDoChat(conversas) {
  const quantas = (conversas || []).filter((c) => (c.nao_lidas || 0) > 0).length;
  window.dispatchEvent(new CustomEvent(EVENTO_NAO_LIDAS_DO_CHAT, { detail: { conversas: quantas } }));
}


/**
 * O navegador DO COMPUTADOR (no celular quem cuida é o sistema): com ele
 * totalmente fechado nenhum site consegue avisar; Edge e Chrome avisam se
 * puderem continuar rodando em segundo plano (pedido do Igor, 2026-11-03).
 * Devolve "edge", "chrome", "outro" ou null (celular/tablet).
 */
export function navegadorDoComputador(nav = typeof navigator === "undefined" ? null : navigator) {
  if (!nav) return null;
  const agente = String(nav.userAgent || "");
  const dados = nav.userAgentData;
  if (dados?.mobile || /Android|iPhone|iPad|iPod|Mobile/i.test(agente)) return null;
  const marcas = (dados?.brands || []).map((b) => b.brand);
  if (marcas.includes("Microsoft Edge") || /Edg\//.test(agente)) return "edge";
  if (marcas.includes("Google Chrome") || (/Chrome\//.test(agente) && !/OPR\/|Brave/.test(agente))) return "chrome";
  return "outro";
}

/** Onde ligar, em cada navegador, a opção de continuar rodando com as janelas fechadas. */
export const SEGUNDO_PLANO = Object.freeze({
  edge: {
    endereco: "edge://settings/system",
    caminho: "Configurações → Sistema e desempenho",
    opcao: "Continuar executando extensões e aplicativos em segundo plano quando o Microsoft Edge estiver fechado",
  },
  chrome: {
    endereco: "chrome://settings/system",
    caminho: "Configurações → Sistema",
    opcao: "Continuar executando apps em segundo plano quando o Google Chrome estiver fechado",
  },
});
